import { useState } from "react";
import { Clock } from "lucide-react";

interface DurationPickerProps {
  value: string; // "MM:SS"
  onChange: (value: string) => void;
}

import { WheelPicker } from "../common/WheelPicker";

export function DurationPicker({ value, onChange }: DurationPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [tempM, setTempM] = useState(0);
  const [tempS, setTempS] = useState(0);

  const handleOpen = () => {
    const [currM, currS] = (value || "00:00").split(":").map(Number);
    setTempM(currM || 0);
    setTempS(currS || 0);
    setIsOpen(true);
  };

  const handleSave = () => {
    onChange(`${String(tempM).padStart(2, '0')}:${String(tempS).padStart(2, '0')}`);
    setIsOpen(false);
  };

  return (
    <>
      <div className="relative group cursor-pointer" onClick={handleOpen}>
        <div className="flex h-14 w-full items-center gap-4 rounded-2xl bg-muted/30 px-5 border border-border/40 transition-all group-hover:border-primary/50 group-hover:bg-muted/50 text-left">
          <Clock className="size-5 text-primary" />
          <div className="flex flex-col">
            <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">Duration</span>
            <span className="text-lg font-bold text-foreground tracking-tight">{value}</span>
          </div>
        </div>
      </div>

      {isOpen && (
        <div
          className="fixed inset-0 z-100 flex items-center justify-center p-6 bg-background/80 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={() => setIsOpen(false)}
        >
          <div
            className="w-full max-w-[320px] rounded-[3rem] bg-card border border-border/60 shadow-2xl animate-in zoom-in-95 duration-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8 pb-0 flex items-center justify-between">
              <div className="flex flex-col">
                <h3 className="text-sm font-bold text-foreground">Set Duration</h3>
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground/60 font-medium">Poetic Studio</p>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20">
                <span className="text-xs font-black text-primary tabular-nums">
                  {String(tempM).padStart(2, '0')}:{String(tempS).padStart(2, '0')}
                </span>
              </div>
            </div>

            <div className="relative h-[240px] flex items-center justify-center perspective-[1000px] select-none">
              <div className="absolute inset-x-6 h-12 bg-primary/5 rounded-2xl border-y border-primary/20 pointer-events-none" />

              <div className="flex items-center w-full px-10 gap-2 h-full">
                <WheelPicker value={tempM} onChange={setTempM} label="min" max={59} isOpen={isOpen} />
                <span className="text-xl font-bold opacity-20 mb-6">:</span>
                <WheelPicker value={tempS} onChange={setTempS} label="sec" max={59} isOpen={isOpen} />
              </div>

              <div className="absolute inset-x-0 top-0 h-20 bg-linear-to-b from-card via-card/80 to-transparent pointer-events-none" />
              <div className="absolute inset-x-0 bottom-0 h-20 bg-linear-to-t from-card via-card/80 to-transparent pointer-events-none" />
            </div>

            <div className="p-4 bg-muted/20 grid grid-cols-2 gap-3">
              <button
                onClick={() => setIsOpen(false)}
                className="h-12 rounded-2xl border border-border/60 font-bold text-[10px] uppercase tracking-widest hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="h-12 rounded-2xl bg-primary text-primary-foreground font-bold text-[10px] uppercase tracking-widest hover:opacity-90 transition-opacity shadow-lg shadow-primary/20"
              >
                Apply Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
